import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { CardTextInput, CardTextInputRef } from '../input-text';
import { useCard, useLanguage } from 'src/service';
import { IconButton, RadioTrain } from 'src/component';
import { CardCheckboxGroup } from '../input-checkbox-group';
import { checkDiplayLinkRating, randomPassword } from 'src/util';
import { CloseCircleOutlined, SyncOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useShallow } from 'zustand/react/shallow';
import styled from 'styled-components';
import { StyledInputLabelWithButton } from '../input-panel.styled';
import { Checkbox, Dropdown, Menu, Tooltip } from 'antd';
import { Card, copyrightMap, editionList, FlagIndexMap, CheckboxChangeEvent, getCardFormatMode, NO_STICKER, PUBLIC_PATH, StickerList } from 'src/model';

const StickerButtonList = StickerList.map(({ value }) => ({
    label: value === NO_STICKER
        ? <CloseCircleOutlined />
        : <img alt={value} src={`${PUBLIC_PATH}/asset/image/sticker/sticker-${value.toLowerCase()}.png`} />,
    value,
}));
const StyledFooterInputContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-sm);
    .checkbox-input {
        align-self: center;
        .ant-checkbox + span {
            user-select: none;
        }
    }
    .sticker-input,
    .checkbox-input {
        grid-column: span 2;
    }
    .sticker-input .ant-radio-button-wrapper {
        padding-left: var(--spacing-sm);
        padding-right: var(--spacing-sm);
    }
    .creator-input,
    .first-edition-text {
        .input-label-with-button {
            gap: var(--spacing-xs);
        }
    }
`;
const StyledLinkRatingInputContainer = styled(StyledInputLabelWithButton)`
    .auto-link-rating-input {
        margin-left: var(--spacing-sm);
    }
    .auto-link-rating {
        display: inline-block;
        width: 1.5rem;
        height: 1.5rem;
        line-height: 1;
        text-align: center;
        padding: var(--spacing-xs);
        background: var(--main-level-4);
        border-radius: var(--br-lg);
        font-family: 'RoGSanSrfStd-Bd';
        box-shadow: var(--bs-button);
    }
`;

export type FooterInputGroupRef = {
    setValue: (value: {
        password?: string,
        creator?: string,
        atk?: string,
        def?: string,
        linkRating?: string,
        firstEditionText?: string,
        cornerText?: string,
    }) => void,
};
export type FooterInputGroup = {
    isMonster: boolean,
    showCreativeOption: boolean,
} & Pick<CardTextInput, 'onTakePicker'>;
export const FooterInputGroup = forwardRef<FooterInputGroupRef, FooterInputGroup>(({
    isMonster,
    showCreativeOption,
    onTakePicker,
}, ref) => {
    const language = useLanguage();
    const {
        autoLinkRating,
        format,
        region,
        hasCornerText,
        isFirstEdition,
        isLegacyCard,
        isLink,
        linkRating,
        linkRatingBehavior,
        showDefAndLink,
        sticker,
        getUpdater,
        setCard,
    } = useCard(useShallow(({
        card: {
            isLegacyCard,
            sticker,
            format,
            region,
            flag,
            isLink,
            linkRating,
            linkMap,
            isFirstEdition,
            hasCornerText,
        },
        getUpdater,
        setCard,
    }) => ({
        autoLinkRating: linkMap.length,
        format,
        region,
        hasCornerText,
        isFirstEdition,
        isLegacyCard,
        isLink,
        linkRating,
        linkRatingBehavior: flag[FlagIndexMap['linkRatingBehavior']],
        showDefAndLink: flag[FlagIndexMap['showDefAndLink']] === 1,
        sticker,
        getUpdater,
        setCard,
    })));
    const passwordInputRef = useRef<CardTextInputRef>(null);
    const creatorInputRef = useRef<CardTextInputRef>(null);
    const atkInputRef = useRef<CardTextInputRef>(null);
    const defInputRef = useRef<CardTextInputRef>(null);
    const linkRatingInputRef = useRef<CardTextInputRef>(null);
    const firstEditionTextRef = useRef<CardTextInputRef>(null);
    const cornerTextRef = useRef<CardTextInputRef>(null);

    const showAtkInput = true;
    const showLinkInput = checkDiplayLinkRating(linkRatingBehavior, isLink);
    const showDefInput = showLinkInput
        ? showDefAndLink
        : true;
    const showPadding = [showAtkInput, showDefInput, showLinkInput].filter(entry => entry === true).length % 2 === 1;
    const formatMode = getCardFormatMode(format, region);
    const copyrightList = copyrightMap[formatMode]
        ? copyrightMap[formatMode]
        : copyrightMap.tcg;

    const changeATK = useMemo(() => getUpdater('atk', value => typeof value === 'string' ? value : value), [getUpdater]);
    const changeDEF = useMemo(() => getUpdater('def', value => typeof value === 'string' ? value : value), [getUpdater]);
    const changeLinkRating = useMemo(() => getUpdater('linkRating', value => typeof value === 'string' ? value : value), [getUpdater]);
    const changePassword = useMemo(() => getUpdater('password', undefined, 'debounce'), [getUpdater]);
    const onStickerChange = useMemo(() => getUpdater('sticker'), [getUpdater]);
    const changeCreator = useMemo(() => getUpdater('creator', undefined, 'debounce'), [getUpdater]);
    /** 
     * * In legacy mode: All marks stay at the bottom left of the artwork, and are mutually exclusive in this state. Corner text is swapped to bottom of text effects.
     * * In modern mode, Limited Edition and 1st Edition text are exclusive at the right of password. Duel Terminal and Speed Duel text are exclusive at the bottom left of the artwork. Corner text is placed correctly under the art.
     */
    const onFirstEditionChange = useCallback((e: CheckboxChangeEvent) => setCard(currentCard => {
        const nextValue = e.target.checked;
        let updatedPart: Partial<Card>;

        if (nextValue) {
            if (isLegacyCard) {
                updatedPart = {
                    isDuelTerminalCard: false,
                    isFirstEdition: true,
                    isLimitedEdition: false,
                    isSpeedCard: false,
                };
            } else {
                updatedPart = {
                    isFirstEdition: true,
                    isLimitedEdition: false,
                };
            }
        } else updatedPart = {
            isFirstEdition: false,
        };

        return { ...currentCard, ...updatedPart };
    }), [setCard, isLegacyCard]);
    const onCornerTextChange = useCallback((e: CheckboxChangeEvent) => setCard(currentCard => {
        const nextValue = e.target.checked;
        let updatedPart: Partial<Card>;

        if (nextValue) {
            if (isLegacyCard) {
                updatedPart = {
                    hasCornerText: true,
                };
            } else {
                updatedPart = {
                    isSpeedCard: false,
                    isDuelTerminalCard: false,
                    hasCornerText: true,
                };
            }
        } else updatedPart = {
            hasCornerText: false,
        };

        return { ...currentCard, ...updatedPart };
    }), [setCard, isLegacyCard]);
    const changeFirstEditionText = useMemo(() => getUpdater('firstEditionText', undefined, 'debounce'), [getUpdater]);
    const changeCornerText = useMemo(() => getUpdater('cornerText', undefined, 'debounce'), [getUpdater]);

    useImperativeHandle(ref, () => ({
        setValue: ({ password, creator, atk, def, linkRating, firstEditionText, cornerText }) => {
            if (typeof atk === 'string') atkInputRef.current?.setValue(atk);
            if (typeof cornerText === 'string') cornerTextRef.current?.setValue(cornerText);
            if (typeof creator === 'string') creatorInputRef.current?.setValue(creator);
            if (typeof def === 'string') defInputRef.current?.setValue(def);
            if (typeof firstEditionText === 'string') firstEditionTextRef.current?.setValue(firstEditionText);
            if (typeof linkRating === 'string') linkRatingInputRef.current?.setValue(linkRating);
            if (typeof password === 'string') passwordInputRef.current?.setValue(password);
        }
    }));

    return <StyledFooterInputContainer className="card-footer-input">
        {(isMonster || showCreativeOption) && <>
            {showAtkInput && <CardTextInput ref={atkInputRef}
                id="atk"
                addonBefore={language['input.atk.label']}
                defaultValue={useCard.getState().card.atk}
                onChange={changeATK}
                onTakePicker={onTakePicker}
            />}
            {showDefInput && <CardTextInput ref={defInputRef}
                id="def"
                addonBefore={language['input.def.label']}
                defaultValue={useCard.getState().card.def}
                onChange={changeDEF}
                onTakePicker={onTakePicker}
            />}
            {showPadding && <div />}
            {showLinkInput && <CardTextInput ref={linkRatingInputRef}
                id="link"
                addonBefore={<StyledLinkRatingInputContainer className="input-label-with-button">
                    <div className="input-label">{language['input.link.label']}</div>
                    <Tooltip title={language['input.link.auto.tooltip']}>
                        <div className="auto-link-rating-input">
                            {(typeof linkRating === 'string' && linkRating.length > 0)
                                ? <Checkbox
                                    onChange={e => {
                                        if (e.target.checked) {
                                            changeLinkRating('');
                                            linkRatingInputRef.current?.setValue('');
                                        }
                                    }}
                                />
                                : <div className="auto-link-rating">
                                    {isLink ? autoLinkRating : '-'}
                                </div>}
                        </div>
                    </Tooltip>
                </StyledLinkRatingInputContainer>}
                defaultValue={`${useCard.getState().card.linkRating}`}
                onChange={changeLinkRating}
                onTakePicker={onTakePicker}
                placeholder={language['input.link.custom.placeholder']}
            />}
        </>}
        <CardTextInput ref={passwordInputRef}
            id="password"
            addonBefore={<StyledInputLabelWithButton className="input-label-with-button">
                <div className="input-label">{language['input.password.label']}</div>
                <IconButton
                    onClick={() => passwordInputRef.current?.setValue(randomPassword())}
                    Icon={SyncOutlined}
                    tooltipProps={{ overlay: language['button.randomize.label'] }}
                />
            </StyledInputLabelWithButton>}
            defaultValue={useCard.getState().card.password}
            onChange={changePassword}
            onTakePicker={onTakePicker}
        />
        <CardTextInput ref={creatorInputRef}
            id="creator"
            addonBefore={<StyledInputLabelWithButton className="input-label-with-button">
                <div className="input-label">{language['input.copyright.label']}</div>
                <Dropdown
                    placement="topLeft"
                    arrow
                    overlay={<Menu onClick={e => e.domEvent.stopPropagation()}>
                        {copyrightList.map((text, index) => {
                            return <Menu.Item key={`${index}`}
                                onClick={() => {
                                    creatorInputRef.current?.setValue(text);
                                }}
                            >
                                {text}
                            </Menu.Item>;
                        })}
                    </Menu>}
                >
                    <div>
                        <IconButton
                            onClick={() => { }}
                            Icon={UnorderedListOutlined}
                        />
                    </div>
                </Dropdown>
            </StyledInputLabelWithButton>}
            defaultValue={useCard.getState().card.creator}
            onChange={changeCreator}
            onTakePicker={onTakePicker}
        />
        {formatMode !== 'sc' && <CardTextInput ref={firstEditionTextRef}
            id="firstEditionText"
            className="first-edition-text"
            addonBefore={<StyledInputLabelWithButton className="input-label-with-button">
                <div className="input-label">{language['input.1st-edition.label']}</div>
                <Checkbox
                    className="input-1st"
                    onChange={onFirstEditionChange}
                    checked={isFirstEdition}
                    tabIndex={0}
                />
                <Dropdown
                    placement="topLeft"
                    arrow
                    overlay={<Menu onClick={e => e.domEvent.stopPropagation()}>
                        {editionList.map(({ label, value }, index) => {
                            return <Menu.Item key={`${index}`}
                                onClick={() => {
                                    onFirstEditionChange({ target: { checked: true } } as CheckboxChangeEvent);
                                    firstEditionTextRef.current?.setValue(value);
                                }}
                            >
                                {label}
                            </Menu.Item>;
                        })}
                    </Menu>}
                >
                    <div>
                        <IconButton
                            onClick={() => { }}
                            Icon={UnorderedListOutlined}
                        />
                    </div>
                </Dropdown>
            </StyledInputLabelWithButton>}
            defaultValue={useCard.getState().card.firstEditionText}
            onChange={changeFirstEditionText}
            onTakePicker={onTakePicker}
        />}
        <CardTextInput ref={cornerTextRef}
            id="cornerText"
            className="corner-text"
            addonBefore={<StyledInputLabelWithButton className="input-label-with-button">
                <div className="input-label">{language['input.corner.label']}</div>
                &nbsp;
                <Checkbox
                    className="input-corner"
                    onChange={onCornerTextChange}
                    checked={hasCornerText}
                    tabIndex={0}
                />
            </StyledInputLabelWithButton>}
            defaultValue={useCard.getState().card.cornerText}
            onChange={changeCornerText}
            onTakePicker={onTakePicker}
        />
        <CardCheckboxGroup />
        <RadioTrain
            className="sticker-input fill-input-train"
            value={sticker}
            onChange={onStickerChange}
            optionList={StickerButtonList}
        >
            <span>{language['input.sticker.label']}</span>
        </RadioTrain>
    </StyledFooterInputContainer>;
});
